import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ArrowRight, Target, TrendingUp, GraduationCap, Wrench, BookOpen, Briefcase, Users, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/layout/Layout';
import StatsCounter from '../components/shared/StatsCounter';
import TestimonialsCarousel from '../components/shared/TestimonialsCarousel';
import {
  statistics,
  services,
  achievements,
  testimonials,
  futureGoals,
  heroImages,
  partnerLogos
} from '../data/mock';

const iconMap = {
  GraduationCap,
  Wrench,
  BookOpen,
  Briefcase,
  Users
};

const HomePage = () => {
  const [currentHeroImage, setCurrentHeroImage] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroImage((prev) => (prev + 1) % heroImages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Images */}
        <div className="absolute inset-0">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentHeroImage ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={image}
                alt=""
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/60" />
            </div>
          ))}
        </div>

        {/* Hero Content */}
        <div className="relative max-w-7xl mx-auto px-4 py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Target className="w-4 h-4" />
              <span>Skill India • Atmanirbhar Bharat</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Empowering Rural Youth Through{' '}
              <span className="text-emerald-400">Skills & Apprenticeships</span>
            </h1>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              Park Skill has been working in skill development, education, apprenticeship, and placement for the past 8 years, transforming lives across India.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/careers">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg">
                  Register as Candidate
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/apprenticeship">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900 px-8 py-6 text-lg">
                  Partner with Us
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Hero Image Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentHeroImage(index)}
              className={`w-12 h-1.5 rounded-full transition-all duration-300 ${
                index === currentHeroImage ? 'bg-emerald-500' : 'bg-white/30'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <StatsCounter stats={statistics} />

      {/* Services Overview */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">What We Offer</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">
              Our Services
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Comprehensive skill development solutions for rural youth and establishment partnerships.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(0, 3).map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <div
                  key={service.id}
                  className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
                  </div>
                  <div className="relative -mt-12 px-6 pb-6">
                    <div className="w-14 h-14 rounded-xl bg-blue-700 flex items-center justify-center mb-4 group-hover:bg-emerald-600 transition-colors">
                      {IconComponent && <IconComponent className="w-7 h-7 text-white" />}
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{service.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{service.description}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link to="/services">
              <Button variant="outline" className="border-blue-700 text-blue-700 hover:bg-blue-50">
                View All Services
                <ChevronRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Our Achievements</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">
              Affiliations & Recognition
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {achievements.map((achievement) => (
              <div
                key={achievement.id}
                className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow text-center"
              >
                <img
                  src={partnerLogos[achievement.logo]}
                  alt={achievement.title}
                  className="h-20 w-auto mx-auto mb-4 bg-slate-50 rounded-lg p-2"
                />
                <h3 className="font-bold text-slate-900 mb-2">{achievement.title}</h3>
                {achievement.ministry && (
                  <span className="inline-block bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full mb-2">{achievement.ministry}</span>
                )}
                <p className="text-slate-600 text-sm">{achievement.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Future Goals Section */}
      <section className="py-20 bg-gradient-to-br from-blue-700 to-slate-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">Our Vision</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-2 mb-4">
              Future Goals
            </h2>
            <p className="text-blue-200 max-w-2xl mx-auto">
              Our ambitious targets for the next 5 years to expand our impact across India.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {futureGoals.map((goal, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center border border-white/20"
              >
                <TrendingUp className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                <div className="text-5xl font-bold text-white mb-2">
                  {goal.target.toLocaleString('en-IN')}
                </div>
                <p className="text-blue-200">{goal.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialsCarousel testimonials={testimonials} />

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {/* For Candidates */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-800 p-8 md:p-12">
              <div className="relative z-10">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  Are You Looking for Skill Training?
                </h3>
                <ul className="space-y-3 mb-8">
                  {['Free skill training under PMKVY', 'Apprenticeship opportunities', 'Job placement assistance', 'Government certified courses'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-emerald-100">
                      <CheckCircle2 className="w-5 h-5 text-white" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/careers">
                  <Button size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50">
                    Register as Candidate
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/10 rounded-full" />
              <div className="absolute -top-10 -right-20 w-60 h-60 bg-white/5 rounded-full" />
            </div>

            {/* For Establishments */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-700 to-slate-900 p-8 md:p-12">
              <div className="relative z-10">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  Partner with Us as an Establishment
                </h3>
                <ul className="space-y-3 mb-8">
                  {['Access to skilled manpower', 'Government stipend reimbursement', 'Tax benefits under IT Act', 'Dedicated support team'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-blue-100">
                      <CheckCircle2 className="w-5 h-5 text-white" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/apprenticeship">
                  <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-50">
                    Partner with Us
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/10 rounded-full" />
              <div className="absolute -top-10 -right-20 w-60 h-60 bg-white/5 rounded-full" />
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
